import React, { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Button,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useCustomForm from "../../../../../hooks/useForm";
import CustomDatePicker from "../../../../../components/DatePicker";
import { useBuildings } from "../../../../../reactQuery/building";
import { createHPDTicket, updateHPDTicket } from "../../../../../sevices/violationsApi";
import useCustomMutate from "../../../../../hooks/useMutate";
import "./ticket-form.scss";
import SearchList from "../../../../../components/Search/SearchList";
import { useViolationCodes } from "../../../../../reactQuery/violations";
import { hpdTicketSchema } from "../../../../../schemas/hpdTicketSchema";

const TicketForm = ({ initialValues = {}, onClose, isEdit = false }) => {
  const {
    register,
    handleSubmit,
    errors,
    setValue,
    watch,
    control,
    Controller,
    reset
  } = useCustomForm(initialValues, hpdTicketSchema);

  const { responseData: buildings } = useBuildings();
  const { responseData: violationCodes } = useViolationCodes();
  const [units, setUnits] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Watch for form field changes
  const selectedBuilding = watch("building_id");

  // Handle mutations
  const { mutate, isLoading } = useCustomMutate(
    isEdit ? updateHPDTicket : createHPDTicket,
    ["hpd-tickets"],
    () => {
      reset();
      onClose();
    }
  );

  const allViolationCodes = Object.values(JSON.parse(violationCodes?.value ||   "{}")).flat().map(
    (key) => ({
      value: key,
      label: key,
    })
  );

  console.log(allViolationCodes, violationCodes?.value);

  // Update units when building changes
  useEffect(() => {
    if (selectedBuilding && buildings) {
      const building = buildings.find(b => b.building_id === selectedBuilding);
      if (building && building.units) {
        setUnits(building.units.map(unit => ({
          value: unit.unit_id,
          label: unit.unit_number
        })));
      } else {
        setUnits([]);
      }
      if(building){
        setValue("client_id", building.client_id);
      }
    }
  }, [selectedBuilding, buildings]);

  // Transform buildings data for select options
  const buildingOptions = buildings?.map(building => ({
    value: building.building_id,
    label: building.address
  })) || [];


  // Render select field
  const renderSelectField = (field) => {
    return (
      <FormControl
        fullWidth
        error={errors?.[field.name] ? true : false}
      >
        <InputLabel shrink>{field.label}</InputLabel>
        <Controller
          name={field.name}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select
              {...register(
                field.name,
                field.isRequired && {
                  required: `${field.label} is required`,
                }
              )}
              value={value || ""}
              defaultValue={initialValues[field.name] || ""}
              className="select-box"
              displayEmpty
              disabled={field?.disabled || false}
              endAdornment={
                watch(field.name) && (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setValue(field.name, "");
                        onChange("");
                      }}
                      aria-label="clear"
                      className="margin-right-1"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="" disabled>
                <em className="placeholder-select">
                  {field.placeholder}
                </em>
              </MenuItem>
              {field.search ? (
                <SearchList
                  onSearch={(val) => setSearchQuery(val)}
                  searchQuery={searchQuery}
                />
              ) : null}
              {field.search
                ? field.options
                    ?.filter((item) =>
                      item.label
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                    )
                    ?.map((option, idx) => (
                      <MenuItem
                        value={option.value}
                        key={idx}
                        className="dropdown-menu-options"
                        onClick={() => setSearchQuery("")}
                      >
                        <p className="dropdown-options">
                          {option.label}
                        </p>
                      </MenuItem>
                    )) || []
                : field.options?.map((option, idx) => (
                    <MenuItem
                      value={option.value}
                      key={idx}
                      className="dropdown-menu-options"
                    >
                      <p className="dropdown-options">
                        {option.label}
                      </p>
                    </MenuItem>
                  )) || []}
            </Select>
          )}
        />
        <FormHelperText>
          {errors?.[field.name] && errors?.[field.name].message}
        </FormHelperText>
      </FormControl>
    );
  };

  console.log(errors);

  // Submit handler
  const onSubmit = (data) => {
    console.log(data);
    mutate(isEdit ? { id: initialValues.id, data } : data);
  };

  return (
    <div className="hpd-ticket-form-container">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Date Field */}
          <Grid item xs={12} md={6}>
            <CustomDatePicker
              label={"Date"}
              defaultValue={initialValues["date"] || ""}
              errors={errors}
              field={{ name: "date", label: "Date" }}
              setValue={setValue}
              excludeDates={[]}
              {...register("date")}
              isShring={true}
              placeholder={"Select date"}
              disabledPast={false}
              watch={watch}
            />
          </Grid>

          {/* Violation Code Field */}
          <Grid item xs={12} md={6}>
            {renderSelectField({
              name: "violation_code",
              label: "Violation Code",
              placeholder: "Select violation code",
              options: allViolationCodes,
              isRequired: true,
              search: true,
            })}
          </Grid>

          {/* Building Field */}
          <Grid item xs={12} md={6}>
            {renderSelectField({
              name: "building_id",
              label: "Building",
              placeholder: "Select building",
              options: buildingOptions,
              isRequired: true,
              search: true,
            })}
          </Grid>

          {/* Apartment/Unit Field */}
          <Grid item xs={12} md={6}>
            {renderSelectField({
              name: "unit_id",
              label: "Apt#",
              placeholder: "Select unit",
              options: units,
              isRequired: true,
              disabled: !selectedBuilding || units.length === 0,
            })}
          </Grid>

          {/* Violation # Field */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Enter violation number"
              label="Violation #"
              type="number"
              InputLabelProps={{ shrink: true }}
              {...register("violation_id")}
              error={Boolean(errors.violation_id)}
              helperText={errors.violation_id?.message}
            />
          </Grid>

          {/* Correct Date Field */}
          <Grid item xs={12} md={4}>
            <CustomDatePicker
              label={"Correct Date"}
              defaultValue={initialValues["correct_by_date"] || ""}
              errors={errors}
              field={{ name: "correct_by_date", label: "Correct Date" }}
              setValue={setValue}
              excludeDates={[]}
              {...register("correct_by_date")}
              isShring={true}
              placeholder={"Select correct date"}
              disabledPast={false}
              watch={watch}
            />
          </Grid>

          {/* Certify Date Field */}
          <Grid item xs={12} md={4}>
            <CustomDatePicker
              label={"Certify Date"}
              defaultValue={initialValues["certify_by_date"] || ""}
              errors={errors}
              field={{ name: "certify_by_date", label: "Certify Date" }}
              setValue={setValue}
              excludeDates={[]}
              {...register("certify_by_date")}
              isShring={true}
              placeholder={"Select certify date"}
              disabledPast={false}
              watch={watch}
            />
          </Grid>

          {/* Violation Description Field */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Start typing..."
              label="Violation Description"
              InputLabelProps={{ shrink: true }}
              {...register("description")}
              error={Boolean(errors.description)}
              helperText={errors.description?.message}
            />
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12} className="form-actions">
            <Button
              variant="contained"
              color="primary"
              type="submit"
              className="create-button"
            >
              {isEdit ? "Update" : "Create"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default TicketForm; 